import { Globe, Footprints, Split } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PersonaVoice } from "@/components/ui/persona-voice";
import { SeverityDot, severityTone } from "@/components/ui/severity-dot";
import type { Severity } from "@/components/finding-badges";
import { StepThumbnail } from "@/components/walkthrough/step-screenshot";
import { JourneyStatusBadge } from "./journey-status-badge";
import type { SelectedStepComparison } from "./comparison-types";
import type { AlignedJourneyStep } from "./journey-trace-diff";

export interface JourneyStepCardProps {
  step: AlignedJourneyStep;
  baseRunNumber?: number;
  currentRunNumber?: number;
  activeBaseRunId: string | null;
  currentWalkthroughId: string;
  onOpenFinding: (id: string) => void;
  onSelectStepComparison: (step: SelectedStepComparison) => void;
}

export function JourneyStepCard({
  step,
  baseRunNumber,
  currentRunNumber,
  activeBaseRunId,
  currentWalkthroughId,
  onOpenFinding,
  onSelectStepComparison,
}: JourneyStepCardProps) {
  const bStep = step.baselineStep;
  const cStep = step.currentStep;
  const baselineFindings = step.baselineFindings ?? [];
  const currentFindings = step.currentFindings ?? [];
  const route = cStep?.route || bStep?.route || "—";
  const hasBothScreenshots = Boolean(bStep && cStep);

  return (
    <article className="py-5 first:pt-1 space-y-3">
      {/* Step Row Header */}
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 flex-wrap min-w-0">
            <span className="font-mono text-xs font-semibold text-foreground tabular-nums">
              #{step.index}
            </span>

            {route !== "—" && (
              <div className="flex items-center gap-1.5 text-xs font-mono text-muted-foreground truncate max-w-sm">
                <Globe className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{route}</span>
              </div>
            )}

            <JourneyStatusBadge status={step.status} />
          </div>

          {hasBothScreenshots && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 shrink-0 text-xs gap-1.5"
              onClick={() =>
                onSelectStepComparison({
                  index: step.index,
                  route,
                  status: step.status,
                  baselineStep: bStep,
                  currentStep: cStep,
                })
              }
            >
              <Split className="size-3.5" />
              <span>Compare screenshots</span>
            </Button>
          )}
        </div>

        {step.summary &&
          step.summary !== "Clean" &&
          baselineFindings.length === 0 &&
          currentFindings.length === 0 && (
            <p className="text-xs text-muted-foreground line-clamp-1">
              {step.summary}
            </p>
          )}
      </div>

      {/* Step Row Body: 2-Column Split */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Column 1: Baseline */}
        <div className="space-y-3 flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground tabular-nums">
                Run #{baseRunNumber ?? "baseline"}{" "}
                {bStep ? `· Step ${bStep.step_number}` : ""}
              </span>
              {!bStep && (
                <span className="text-xs text-muted-foreground">
                  Not reached in baseline
                </span>
              )}
            </div>

            {bStep ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  {bStep.action}
                </p>

                {bStep.note && (
                  <PersonaVoice size="sm" className="text-xs">
                    "{bStep.note.replace(/^Sam:\s*"/, "").replace(/"$/, "")}"
                  </PersonaVoice>
                )}

                {baselineFindings.length > 0 && (
                  <ul className="space-y-1 pt-1">
                    {baselineFindings.map((f) => (
                      <li key={f.id}>
                        <button
                          type="button"
                          onClick={() => onOpenFinding(f.id)}
                          className="flex w-full items-start gap-2 rounded-sm bg-muted/40 px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted/70 cursor-pointer"
                        >
                          <SeverityDot
                            tone={severityTone(f.severity as Severity)}
                            className="mt-1 shrink-0"
                          />
                          <span className="min-w-0 line-clamp-2 text-foreground">
                            <span className="font-semibold capitalize">
                              {f.type}:
                            </span>{" "}
                            <span className="text-muted-foreground">
                              {f.title || f.ui_fact || f.id}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="min-h-[140px] flex flex-col items-center justify-center rounded-sm bg-muted/30 p-4 text-center">
                <Footprints className="size-5 text-muted-foreground mb-1.5" />
                <p className="text-xs font-medium text-muted-foreground">
                  Step not reached in baseline run
                </p>
              </div>
            )}
          </div>

          {bStep && (
            <div className="pt-1">
              <div className="w-full rounded-sm border border-border/70 overflow-hidden bg-background">
                <StepThumbnail
                  walkthroughId={activeBaseRunId}
                  stepNumber={bStep.step_number}
                  alt={`Baseline Step ${bStep.step_number}`}
                  className="aspect-video w-full object-cover"
                />
              </div>
            </div>
          )}
        </div>

        {/* Column 2: Current */}
        <div className="space-y-3 flex flex-col justify-between">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-foreground tabular-nums">
                Run #{currentRunNumber ?? "current"}{" "}
                {cStep ? `· Step ${cStep.step_number}` : ""}
              </span>
              {!cStep && (
                <span className="text-xs text-muted-foreground">
                  Bypassed in current run
                </span>
              )}
            </div>

            {cStep ? (
              <div className="space-y-2">
                <p className="text-sm font-medium text-foreground">
                  {cStep.action}
                </p>

                {cStep.note && (
                  <PersonaVoice size="sm" className="text-xs">
                    "{cStep.note.replace(/^Sam:\s*"/, "").replace(/"$/, "")}"
                  </PersonaVoice>
                )}

                {currentFindings.length > 0 && (
                  <ul className="space-y-1 pt-1">
                    {currentFindings.map((f) => (
                      <li key={f.id}>
                        <button
                          type="button"
                          onClick={() => onOpenFinding(f.id)}
                          className="flex w-full items-start gap-2 rounded-sm bg-muted/40 px-2.5 py-1.5 text-left text-xs transition-colors hover:bg-muted/70 cursor-pointer"
                        >
                          <SeverityDot
                            tone={severityTone(f.severity as Severity)}
                            className="mt-1 shrink-0"
                          />
                          <span className="min-w-0 line-clamp-2 text-foreground">
                            <span className="font-semibold capitalize">
                              {f.type}:
                            </span>{" "}
                            <span className="text-muted-foreground">
                              {f.title || f.ui_fact || f.id}
                            </span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="min-h-[140px] flex flex-col items-center justify-center rounded-sm bg-muted/30 p-4 text-center">
                <Footprints className="size-5 text-muted-foreground mb-1.5" />
                <p className="text-xs font-medium text-muted-foreground">
                  Step bypassed or skipped in current run
                </p>
              </div>
            )}
          </div>

          {cStep && (
            <div className="pt-1">
              <div className="w-full rounded-sm border border-border/70 overflow-hidden bg-background">
                <StepThumbnail
                  walkthroughId={currentWalkthroughId}
                  stepNumber={cStep.step_number}
                  alt={`Current Step ${cStep.step_number}`}
                  className="aspect-video w-full object-cover"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </article>
  );
}
